import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { makeStyles } from '@material-ui/core'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import EditIcon from '@material-ui/icons/Edit'
import PauseIcon from '@material-ui/icons/Pause'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import StopIcon from '@material-ui/icons/Stop'
import calendarLiveStreamsStyles, { LIVE_PULSE_MS, SPIN_MS } from './calendarLiveStreams.styles'
import { useTeamCalendarContext } from '../../../context/useTeamCalendarContext'
import useHistoryPush from '../../../hooks/useHistoryPush'
import useQueryParams from '../../../hooks/useQueryParams'
import useSyncedAnimationDelay from '../../../hooks/useSyncedAnimationDelay'
import pauseLivestream from '../../../Apis/livestream/pauseLivestream'
import resumeLivestream from '../../../Apis/livestream/resumeLivestream'
import stopLivestream from '../../../Apis/livestream/stopLivestream'
import QueryKeys from '../../../utils/constants/queryKeys'
import BROADCAST_STATES from '../../../utils/constants/broadcastStates'
import broadcastEventType from '../../../types/broadcastEventType'
import parseTime from '../../../utils/helpers/dateHelpers/parseTime'

const useStyles = makeStyles(calendarLiveStreamsStyles)

const CalendarLiveStreamRow = ({
  broadcastEvent,
  isConfirmingStop,
  onRequestStop,
  onCancelStop,
}) => {
  const { id, title, broadcast, startDateTime, endDateTime, type } = broadcastEvent || {}
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { setSelectedEventType, setClickedEventId } = useTeamCalendarContext()
  const { redirect, pathname } = useHistoryPush()
  const { org: orgId } = useQueryParams()
  const classes = useStyles()

  const refreshStreams = () => queryClient.invalidateQueries(QueryKeys.GET_RUNNING_BROADCASTS_EVENTS)

  const pauseTheLivestream = useMutation(() => pauseLivestream(broadcast?.id, orgId), {
    onSuccess: refreshStreams,
  })
  const resumeTheLivestream = useMutation(() => resumeLivestream(broadcast?.id, orgId), {
    onSuccess: refreshStreams,
  })
  // The call returns long before the broadcast reports "deleting", so remember that this row's
  // stop was asked for. Without it the row falls back to Edit/Pause/Stop for those few seconds,
  // which reads as "that didn't work" and invites a second press of a destructive button.
  const [stopRequested, setStopRequested] = useState(false)
  const stopTheLivestream = useMutation(() => stopLivestream(broadcast?.id, orgId), {
    onSuccess: () => {
      setStopRequested(true)
      onCancelStop()
      return refreshStreams()
    },
  })

  const isPaused = broadcast?.state === BROADCAST_STATES.paused
  const isStarting = broadcast?.state === BROADCAST_STATES.creating
  const isLiveState =
    broadcast?.state === BROADCAST_STATES.running || broadcast?.state === BROADCAST_STATES.paused
  const isStopping =
    broadcast?.state === BROADCAST_STATES.deleting || stopTheLivestream.isLoading || stopRequested

  // Once the broadcast has left the live states the server has caught up and can speak for
  // itself; holding the flag past that point would outlast what it was covering for.
  useEffect(() => {
    if (stopRequested && !isLiveState) setStopRequested(false)
  }, [stopRequested, isLiveState])
  const isTogglingPause = pauseTheLivestream.isLoading || resumeTheLivestream.isLoading

  const handleTogglePause = () => {
    if (isTogglingPause) return
    if (isPaused) {
      resumeTheLivestream.mutate()
      return
    }
    pauseTheLivestream.mutate()
  }

  const handleStop = () => {
    if (stopTheLivestream.isLoading) return
    stopTheLivestream.mutate()
  }

  // Same route the event modal's pencil takes: the drawer opens off `/<calendar>/:eventId`.
  // The id has to go through the context too — the drawer builds its form from the event that
  // clickedEventId fetches, and the route only seeds that on a fresh mount, so navigating here
  // from an already-open calendar would otherwise open an empty create form.
  const handleEdit = () => {
    setSelectedEventType(type)
    setClickedEventId(id)
    // Built off the first path segment, the way the drawer's own close does, so an edit
    // fired while the path already carries an event id doesn't nest a second one.
    redirect(`/${pathname.split('/')[1]}/${id}`, [{ eventType: type?.code }])
  }

  const dotClass = () => {
    if (isStarting) return classes.dotStarting
    if (isStopping) return classes.dotStopping
    if (isPaused) return classes.dotPaused
    return classes.dotLive
  }

  // Rows enter these states at different moments, so left alone each dot pulses and each
  // spinner turns on its own schedule; locking them to one clock keeps the column in step.
  const isPulsing = !isStarting && !isStopping && !isPaused
  const pulseStyle = useSyncedAnimationDelay(LIVE_PULSE_MS, isPulsing)
  const spinStyle = useSyncedAnimationDelay(SPIN_MS, isStarting || isStopping)

  return (
    <div className={classes.row}>
      <span className={classNames(classes.dot, dotClass())} style={pulseStyle} />

      <div className={classes.titleColumn}>
        <span className={classes.title}>{title}</span>
        <span className={classes.timeRange}>
          {`${parseTime(startDateTime)} – ${parseTime(endDateTime)}`}
        </span>
      </div>

      {/* Whether the stream reaches an audience or is only being kept for later. */}
      {broadcast?.isStreamedToYoutube && (
        <span className={classNames(classes.badge, classes.badgeBroadcast)}>
          {translate('ra.text.broadcasting')}
        </span>
      )}
      {!broadcast?.isStreamedToYoutube && broadcast?.isRecorded && (
        <span className={classNames(classes.badge, classes.badgeRecording)}>
          {translate('ra.text.recordingOnly')}
        </span>
      )}

      <span className={classes.spacer} />

      {(isStarting || isStopping) && (
        <div className={classes.transient}>
          <span
            className={classNames(
              classes.spinner,
              isStarting ? classes.spinnerStarting : classes.spinnerStopping,
            )}
            style={spinStyle}
          />
          {isStarting ? translate('ra.text.startingStream') : translate('ra.text.stoppingStream')}
        </div>
      )}

      {!isStarting && !isStopping && isConfirmingStop && (
        <div className={classes.confirm}>
          <span className={classes.confirmText}>{translate('ra.text.stopThisStream')}</span>
          <button type='button' className={classes.confirmCancel} onClick={onCancelStop}>
            {translate('ra.buttons.cancelSmall')}
          </button>
          <button
            type='button'
            className={classes.confirmStop}
            onClick={handleStop}
            disabled={stopTheLivestream.isLoading}
          >
            {translate('ra.buttons.stopStream')}
          </button>
        </div>
      )}

      {!isStarting && !isStopping && !isConfirmingStop && (
        <div className={classes.actions}>
          <button
            type='button'
            className={classes.button}
            onClick={handleEdit}
            title={translate('ra.buttons.edit')}
          >
            <EditIcon className={classes.buttonIcon} />
            {translate('ra.buttons.edit')}
          </button>
          <button
            type='button'
            className={classes.button}
            onClick={handleTogglePause}
            disabled={isTogglingPause}
            title={
              isPaused ? translate('ra.buttons.resumeSmall') : translate('ra.buttons.pauseSmall')
            }
          >
            {isPaused ? (
              <PlayArrowIcon className={classes.buttonIcon} />
            ) : (
              <PauseIcon className={classes.buttonIcon} />
            )}
            {isPaused ? translate('ra.buttons.resumeSmall') : translate('ra.buttons.pauseSmall')}
          </button>
          <button
            type='button'
            className={classNames(classes.button, classes.stopButton)}
            onClick={onRequestStop}
            title={translate('ra.buttons.stopSmall')}
          >
            <StopIcon className={classes.buttonIcon} />
            {translate('ra.buttons.stopSmall')}
          </button>
        </div>
      )}
    </div>
  )
}

CalendarLiveStreamRow.propTypes = {
  broadcastEvent: broadcastEventType,
  isConfirmingStop: PropTypes.bool,
  onRequestStop: PropTypes.func,
  onCancelStop: PropTypes.func,
}

export default CalendarLiveStreamRow
